/** 投资经验类型 code → 年资提交字段（与 /user/account/career/save 对齐） */
export type InvestmentExperienceYearField =
  | 'stockExperience'
  | 'futuresExperience'
  | 'optionsExperience'
  | 'warrantBullBearExperience'
  | 'fxExperience'
  | 'bullionExperience'
  | 'fundStructuredProductExperience'
  | 'virtualAssetExperience';

export const INVESTMENT_YEAR_FIELDS: InvestmentExperienceYearField[] = [
  'stockExperience',
  'futuresExperience',
  'optionsExperience',
  'warrantBullBearExperience',
  'fxExperience',
  'bullionExperience',
  'fundStructuredProductExperience',
  'virtualAssetExperience',
];

/** 字典 investmentExperienceType.code → 年资字段（完整 code + 短码） */
const TYPE_CODE_TO_FIELD: Record<string, InvestmentExperienceYearField> = {
  // 完整字典 code
  INVESTMENT_EXPERIENCE_STOCK: 'stockExperience',
  INVESTMENT_EXPERIENCE_SECURITIES: 'stockExperience',
  INVESTMENT_EXPERIENCE_SECURITY: 'stockExperience',
  INVESTMENT_EXPERIENCE_FUTURES: 'futuresExperience',
  INVESTMENT_EXPERIENCE_FUTURE: 'futuresExperience',
  INVESTMENT_EXPERIENCE_OPTIONS: 'optionsExperience',
  INVESTMENT_EXPERIENCE_OPTION: 'optionsExperience',
  INVESTMENT_EXPERIENCE_WARRANT: 'warrantBullBearExperience',
  INVESTMENT_EXPERIENCE_WARRANTS: 'warrantBullBearExperience',
  INVESTMENT_EXPERIENCE_WARRANT_BULL_BEAR: 'warrantBullBearExperience',
  INVESTMENT_EXPERIENCE_FX: 'fxExperience',
  INVESTMENT_EXPERIENCE_FOREX: 'fxExperience',
  INVESTMENT_EXPERIENCE_FOREIGN_EXCHANGE: 'fxExperience',
  INVESTMENT_EXPERIENCE_BULLION: 'bullionExperience',
  INVESTMENT_EXPERIENCE_PRECIOUS_METAL: 'bullionExperience',
  INVESTMENT_EXPERIENCE_PRECIOUS_METALS: 'bullionExperience',
  INVESTMENT_EXPERIENCE_FUND: 'fundStructuredProductExperience',
  INVESTMENT_EXPERIENCE_FUNDS: 'fundStructuredProductExperience',
  INVESTMENT_EXPERIENCE_FUND_STRUCTURED_PRODUCT: 'fundStructuredProductExperience',
  INVESTMENT_EXPERIENCE_VIRTUAL_ASSET: 'virtualAssetExperience',
  // 短码兼容
  STOCK: 'stockExperience',
  SECURITIES: 'stockExperience',
  SECURITY: 'stockExperience',
  FUTURES: 'futuresExperience',
  FUTURE: 'futuresExperience',
  OPTIONS: 'optionsExperience',
  OPTION: 'optionsExperience',
  WARRANT: 'warrantBullBearExperience',
  WARRANTS: 'warrantBullBearExperience',
  WARRANT_BULL_BEAR: 'warrantBullBearExperience',
  FX: 'fxExperience',
  FOREX: 'fxExperience',
  FOREIGN_EXCHANGE: 'fxExperience',
  BULLION: 'bullionExperience',
  PRECIOUS_METAL: 'bullionExperience',
  PRECIOUS_METALS: 'bullionExperience',
  FUND: 'fundStructuredProductExperience',
  FUNDS: 'fundStructuredProductExperience',
  FUND_STRUCTURED_PRODUCT: 'fundStructuredProductExperience',
  VIRTUAL_ASSET: 'virtualAssetExperience',
  VIRTUAL: 'virtualAssetExperience',
};

const NONE_TYPE_CODES = new Set([
  'NONE',
  'NO',
  'NO_EXPERIENCE',
  'NIL',
  'NOTHING',
  'NONE_EXPERIENCE',
  'INVESTMENT_EXPERIENCE_NONE',
  'INVESTMENT_EXPERIENCE_NO',
  'INVESTMENT_EXPERIENCE_NO_EXPERIENCE',
  'INVESTMENT_EXPERIENCE_NIL',
  'INVESTMENT_EXPERIENCE_NOTHING',
]);

const resolveLabelText = (label: unknown): string => {
  if (typeof label === 'function') {
    try {
      return String(label() ?? '');
    } catch {
      return '';
    }
  }
  return String(label ?? '');
};

/** 「没有」经验类型：无年资下拉，且与其它类型互斥 */
export const isNoneExperienceType = (code: string, label?: unknown): boolean => {
  const c = String(code || '').toUpperCase();
  if (NONE_TYPE_CODES.has(c)) return true;
  const text = resolveLabelText(label);
  return /没有|沒有|^none$/i.test(text.trim());
};

/** 根据字典 code / 文案解析对应年资字段；「没有」返回 null */
export const resolveExperienceYearField = (
  code: string,
  label?: unknown
): InvestmentExperienceYearField | null => {
  if (isNoneExperienceType(code, label)) return null;
  const c = String(code || '').toUpperCase();
  if (TYPE_CODE_TO_FIELD[c]) return TYPE_CODE_TO_FIELD[c];
  const short = c.replace(/^INVESTMENT_EXPERIENCE_/, '');
  if (short !== c && TYPE_CODE_TO_FIELD[short]) return TYPE_CODE_TO_FIELD[short];

  const text = resolveLabelText(label);
  if (/证券|證券|股票|stock|securit/i.test(text)) return 'stockExperience';
  if (/期权|期權|option/i.test(text)) return 'optionsExperience';
  if (/期货|期貨|future/i.test(text)) return 'futuresExperience';
  if (/认股权证|認股權證|认股证|認股證|warrant|牛熊/i.test(text)) return 'warrantBullBearExperience';
  if (/外汇|外匯|forex|\bfx\b/i.test(text)) return 'fxExperience';
  if (/贵金属|貴金屬|bullion|precious/i.test(text)) return 'bullionExperience';
  if (/基金|fund|结构性|結構性/i.test(text)) return 'fundStructuredProductExperience';
  if (/虚拟资产|虛擬資產|virtual.?asset/i.test(text)) return 'virtualAssetExperience';
  return null;
};

export const clearAllExperienceYears = (form: Record<string, any>) => {
  for (const field of INVESTMENT_YEAR_FIELDS) {
    form[field] = '';
  }
};

type DictOption = { label: any; value: string | number; other?: any };

/**
 * 为某个年资下拉生成独立 options 副本，避免多个 SelectBox 共用同一引用产生联动。
 * 按 yearField 缓存；源列表为空或长度变化时重新拷贝。
 */
const yearOptionsCache: Partial<Record<InvestmentExperienceYearField, DictOption[]>> = {};

export const getInvestmentExperienceOptionsCopy = (
  yearField: InvestmentExperienceYearField,
  source: DictOption[] | undefined | null
): DictOption[] => {
  const list = Array.isArray(source) ? source : [];
  const cached = yearOptionsCache[yearField];
  if (cached && list.length > 0 && cached.length === list.length) return cached;
  if (!list.length) return cached ?? [];
  yearOptionsCache[yearField] = list.map((o) => ({
    label: o.label,
    value: o.value,
    other: o.other,
  }));
  return yearOptionsCache[yearField]!;
};
